import React from 'react';

const VarianceHeatMap = ({ varianceAnalysis, problemAnalysis, handleDrillDown }) => {
  if (!varianceAnalysis || !problemAnalysis?.topInaccurateLocations) {
    return (
      <div className="flex flex-col justify-center items-center py-12">
        <div className="text-6xl mb-4 text-gray-300">🗺️</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Variance Heat Map</h3>
        <p className="text-gray-600">No variance data available</p>
        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or date range</p>
      </div>
    );
  }

  const getVarianceColor = (accuracy) => {
    if (accuracy >= 95) return 'bg-emerald-400';
    if (accuracy >= 80) return 'bg-amber-300';
    if (accuracy >= 70) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getVarianceIntensity = (accuracy) => {
    const intensity = Math.max(0.3, accuracy / 100);
    return { opacity: intensity };
  };

  return (
    <div className="bg-white">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Location Variance Heat Map</h3>
            <p className="text-sm text-gray-600">Visual representation of accuracy performance by location</p>
          </div>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <div className="text-sm font-medium text-gray-600 mb-1">Shortages</div>
          <div className="text-2xl font-bold text-red-700">
            {varianceAnalysis.shortageCount || 0}
          </div>
          <div className="text-xs text-red-600">
            ${Math.abs(varianceAnalysis.shortageValue || 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg">
          <div className="text-sm font-medium text-gray-600 mb-1">Overages</div>
          <div className="text-2xl font-bold text-emerald-700">
            {varianceAnalysis.overageCount || 0}
          </div>
          <div className="text-xs text-emerald-600">
            ${Math.abs(varianceAnalysis.overageValue || 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="text-sm font-medium text-gray-600 mb-1">Perfect Matches</div>
          <div className="text-2xl font-bold text-blue-700">
            {varianceAnalysis.perfectMatchCount || 0}
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
          <div className="text-sm font-medium text-gray-600 mb-1">Net Variance</div>
          <div className={`text-2xl font-bold ${
            (varianceAnalysis.netVarianceValue || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'
          }`}>
            ${Math.abs(varianceAnalysis.netVarianceValue || 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Location Heat Map Grid */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {problemAnalysis.topInaccurateLocations.map((location, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-105 ${getVarianceColor(location.accuracy)} text-white shadow-sm`}
              style={getVarianceIntensity(location.accuracy)}
              onClick={() => {
                handleDrillDown('location', location);
              }}
            >
              <div className="font-semibold text-sm truncate mb-2">
                {location.locationName}
              </div>
              <div className="text-xl font-bold mb-1">
                {location.accuracy?.toFixed(1)}%
              </div>
              <div className="text-xs opacity-90">
                {location.skuCount || 0} SKUs
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-emerald-400 rounded shadow-sm"></div>
          <span className="text-gray-700 font-medium">95-100%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-amber-300 rounded shadow-sm"></div>
          <span className="text-gray-700 font-medium">80-94%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-orange-400 rounded shadow-sm"></div>
          <span className="text-gray-700 font-medium">70-79%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-400 rounded shadow-sm"></div>
          <span className="text-gray-700 font-medium">&lt;70%</span>
        </div>
      </div>
    </div>
  );
};

export default VarianceHeatMap;
